import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

const firebaseAdminCredential = () =>
  z.string().transform((val, ctx) => {
    if (val) {
      try {
        return JSON.parse(val) as Record<string, string>;
      } catch (err) {
        ctx.addIssue({
          code: 'custom',
          message: `Invalid Firebase Admin credential: ${err}`,
        });
        return z.NEVER;
      }
    }
  });

export const env = createEnv({
  server: {
    ROLLBAR_SERVER_TOKEN: z.string().optional(),
    OPENAI_API_KEY: z.string().min(1),
    ANTHROPIC_API_KEY: z.string().min(1).optional(),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string().min(1).optional(),
    DEEPSEEK_API_KEY: z.string().min(1).optional(),
    GROQ_API_KEY: z.string().min(1).optional(),

    // Storage/Database
    STORAGE_TYPE: z.enum(['libsql', 'postgres']),
    DATABASE_URL: z.url(),
    DATABASE_AUTH_TOKEN: z.string().optional(),

    // Firebase Admin
    FIREBASE_ADMIN_CREDENTIAL: firebaseAdminCredential(),
    FIREBASE_STORAGE_BUCKET: z.string().optional(),

    // OIDC configuration (backend)
    OIDC_ISSUER: z.url().optional(),
    OIDC_AUDIENCE: z.string().optional(),
    OIDC_JWKS_URI: z.url().optional(),

    // MCP Server configuration
    MCP_CLIENT_ID: z.string(),
    MCP_RESOURCE_URL: z.string(),

    // Task Masters API
    TASK_MASTERS_API_BASE_URL: z.string(),
    TASK_MASTERS_DEBUG: z.stringbool().default(false),

    // GitHub API
    GITHUB_TOKEN: z.string().min(1),

    // Agento API
    AGENTO_API_TOKEN: z.string().optional(),

    // Fireflies API
    FIREFLIES_API_KEY: z.string().min(1).optional(),

    // HubSpot API
    HUBSPOT_ACCESS_TOKEN: z.string().min(1).optional(),

    // BNI API
    BNI_AGENT_URL: z.string().optional(),
    BNI_AGENT_API_TOKEN: z.string().optional(),

    // Mastra Studio
    MASTRA_DEV: z.stringbool().default(false),

    // Slack channel
    SLACK_TASK_MASTERS_BOT_TOKEN: z.string().optional(),
    SLACK_TASK_MASTERS_SIGNING_SECRET: z.string().optional(),
    SLACK_TASK_MASTERS_AGENT_ID: z.string().optional(),

    SLACK_CODE_BOT_TOKEN: z.string().optional(),
    SLACK_CODE_SIGNING_SECRET: z.string().optional(),
    SLACK_CODE_AGENT_ID: z.string().optional(),

    SLACK_COMMERCIAL_BOT_TOKEN: z.string().optional(),
    SLACK_COMMERCIAL_SIGNING_SECRET: z.string().optional(),
    SLACK_COMMERCIAL_AGENT_ID: z.string().optional(),

    SLACK_FINANCIAL_BOT_TOKEN: z.string().optional(),
    SLACK_FINANCIAL_SIGNING_SECRET: z.string().optional(),
    SLACK_FINANCIAL_AGENT_ID: z.string().optional(),

    // Telegram channel
    TELEGRAM_BOT_TOKEN: z.string().optional(),
    TELEGRAM_WEBHOOK_SECRET_TOKEN: z.string().optional(),
    TELEGRAM_AGENT_ID: z.string().optional(),

    // Testing
    TEST_TOKEN: z.string().optional(),
    SHOW_TOKEN: z.stringbool().default(false),

    // Feature flags
    TOOL_SEARCH_ENABLED: z.stringbool().default(false),

    // FFmpeg
    FFMPEG_PATH: z.string().optional(),
  },
  client: {
    // Rollbar
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: z.coerce.number().optional(),
    NEXT_PUBLIC_ROLLBAR_ENV: z.string().optional(),
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    NEXT_PUBLIC_URL: z.string(),

    // Backend
    NEXT_PUBLIC_BACKEND_BASE_URL: z.string(),

    // Auth configuration
    NEXT_PUBLIC_AUTH_PROVIDER: z.enum(['oidc', 'firebase', 'rest']).optional(),

    // OIDC configuration
    NEXT_PUBLIC_OIDC_AUTHORITY: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_OIDC_REDIRECT_URI: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_SCOPES: z.string().optional(), // Space-separated scopes

    // App Masters badge
    NEXT_PUBLIC_TASK_MASTERS_PROJECT_SLUG: z.string().optional(),

    // Build info
    NEXT_PUBLIC_APP_VERSION: z.string().optional(),
    NEXT_PUBLIC_BUILD_DATE: z.string().optional(),
  },
  runtimeEnv: {
    ROLLBAR_SERVER_TOKEN: process.env.ROLLBAR_SERVER_TOKEN,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    DEEPSEEK_API_KEY: process.env.DEEPSEEK_API_KEY,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
    STORAGE_TYPE: process.env.STORAGE_TYPE,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_AUTH_TOKEN: process.env.DATABASE_AUTH_TOKEN,
    FIREBASE_ADMIN_CREDENTIAL: process.env.FIREBASE_ADMIN_CREDENTIAL,
    FIREBASE_STORAGE_BUCKET: process.env.FIREBASE_STORAGE_BUCKET,
    OIDC_ISSUER: process.env.OIDC_ISSUER,
    OIDC_AUDIENCE: process.env.OIDC_AUDIENCE,
    OIDC_JWKS_URI: process.env.OIDC_JWKS_URI,
    MCP_CLIENT_ID: process.env.MCP_CLIENT_ID,
    MCP_RESOURCE_URL: process.env.MCP_RESOURCE_URL,
    TASK_MASTERS_API_BASE_URL: process.env.TASK_MASTERS_API_BASE_URL,
    TASK_MASTERS_DEBUG: process.env.TASK_MASTERS_DEBUG,
    GITHUB_TOKEN: process.env.GITHUB_TOKEN,
    AGENTO_API_TOKEN: process.env.AGENTO_API_TOKEN,
    FIREFLIES_API_KEY: process.env.FIREFLIES_API_KEY,
    HUBSPOT_ACCESS_TOKEN: process.env.HUBSPOT_ACCESS_TOKEN,
    BNI_AGENT_URL: process.env.BNI_AGENT_URL,
    BNI_AGENT_API_TOKEN: process.env.BNI_AGENT_API_TOKEN,
    MASTRA_DEV: process.env.MASTRA_DEV,
    SLACK_TASK_MASTERS_BOT_TOKEN: process.env.SLACK_TASK_MASTERS_BOT_TOKEN,
    SLACK_TASK_MASTERS_SIGNING_SECRET: process.env.SLACK_TASK_MASTERS_SIGNING_SECRET,
    SLACK_TASK_MASTERS_AGENT_ID: process.env.SLACK_TASK_MASTERS_AGENT_ID,
    SLACK_CODE_BOT_TOKEN: process.env.SLACK_CODE_BOT_TOKEN,
    SLACK_CODE_SIGNING_SECRET: process.env.SLACK_CODE_SIGNING_SECRET,
    SLACK_CODE_AGENT_ID: process.env.SLACK_CODE_AGENT_ID,
    SLACK_COMMERCIAL_BOT_TOKEN: process.env.SLACK_COMMERCIAL_BOT_TOKEN,
    SLACK_COMMERCIAL_SIGNING_SECRET: process.env.SLACK_COMMERCIAL_SIGNING_SECRET,
    SLACK_COMMERCIAL_AGENT_ID: process.env.SLACK_COMMERCIAL_AGENT_ID,
    SLACK_FINANCIAL_BOT_TOKEN: process.env.SLACK_FINANCIAL_BOT_TOKEN,
    SLACK_FINANCIAL_SIGNING_SECRET: process.env.SLACK_FINANCIAL_SIGNING_SECRET,
    SLACK_FINANCIAL_AGENT_ID: process.env.SLACK_FINANCIAL_AGENT_ID,
    TELEGRAM_BOT_TOKEN: process.env.TELEGRAM_BOT_TOKEN,
    TELEGRAM_WEBHOOK_SECRET_TOKEN: process.env.TELEGRAM_WEBHOOK_SECRET_TOKEN,
    TELEGRAM_AGENT_ID: process.env.TELEGRAM_AGENT_ID,
    TEST_TOKEN: process.env.TEST_TOKEN,
    SHOW_TOKEN: process.env.SHOW_TOKEN,
    TOOL_SEARCH_ENABLED: process.env.TOOL_SEARCH_ENABLED,
    FFMPEG_PATH: process.env.FFMPEG_PATH,
    NEXT_PUBLIC_BACKEND_BASE_URL: process.env.NEXT_PUBLIC_BACKEND_BASE_URL,
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: process.env.NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN,
    NEXT_PUBLIC_ROLLBAR_ENV: process.env.NEXT_PUBLIC_ROLLBAR_ENV,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_AUTH_PROVIDER: process.env.NEXT_PUBLIC_AUTH_PROVIDER,
    NEXT_PUBLIC_OIDC_AUTHORITY: process.env.NEXT_PUBLIC_OIDC_AUTHORITY,
    NEXT_PUBLIC_OIDC_CLIENT_ID: process.env.NEXT_PUBLIC_OIDC_CLIENT_ID,
    NEXT_PUBLIC_OIDC_REDIRECT_URI: process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI,
    NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI:
      process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI,
    NEXT_PUBLIC_OIDC_SCOPES: process.env.NEXT_PUBLIC_OIDC_SCOPES,
    NEXT_PUBLIC_TASK_MASTERS_PROJECT_SLUG: process.env.NEXT_PUBLIC_TASK_MASTERS_PROJECT_SLUG,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_BUILD_DATE: process.env.NEXT_PUBLIC_BUILD_DATE,
  },
});
